import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { adminApi, catalogApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BG_PRESETS = [
  'from-emerald-500 to-emerald-700',
  'from-slate-800 to-slate-950',
  'from-orange-500 to-rose-600',
  'from-blue-500 to-indigo-700',
  'from-amber-500 to-orange-600',
  'from-pink-500 to-rose-600',
];

const empty = { id: '', title: '', subtitle: '', bg: BG_PRESETS[0] };

const PromosPage = () => {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { try { setPromos(await catalogApi.promos()); } catch { /* */ } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const { id, ...rest } = form;
        await adminApi.updatePromo(editing.id, rest);
        toast.success('Promo updated');
      } else {
        if (!form.id) { toast.error('ID is required'); return; }
        await adminApi.createPromo(form);
        toast.success('Promo created');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo "${p.title}"?`)) return;
    try { await adminApi.deletePromo(p.id); toast.success('Promo deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Promos</h1>
          <p className="text-slate-500 mt-1">Promotional banners shown on the home page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-1.5" />New promo</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? 'Edit promo' : 'Create promo'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>ID</Label><Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} disabled={!!editing} placeholder="p4" className="h-10 mt-1" /></div>
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-10 mt-1" /></div>
              <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="h-10 mt-1" /></div>
              <div>
                <Label>Background gradient</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {BG_PRESETS.map((b) => (
                    <button key={b} onClick={() => setForm({ ...form, bg: b })} type="button"
                      className={`h-12 rounded-lg bg-gradient-to-br ${b} ${form.bg === b ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`} />
                  ))}
                </div>
              </div>
              {form.title && (
                <div className={`mt-2 p-4 rounded-xl bg-gradient-to-br ${form.bg} text-white`}>
                  <div className="font-semibold">{form.title}</div>
                  <div className="text-sm text-white/80">{form.subtitle}</div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white">{editing ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {promos.map((p) => (
          <div key={p.id} className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${p.bg} text-white shadow`}>
            <div className="font-semibold text-lg leading-snug">{p.title}</div>
            <div className="text-sm text-white/80 mt-1">{p.subtitle}</div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="secondary" className="bg-white/15 text-white hover:bg-white/25 border-0 backdrop-blur" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="secondary" className="bg-white/15 text-white hover:bg-white/25 border-0 backdrop-blur" onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PromosPage;
